import { toast } from "react-toastify";
import { SubscriptionPlan, PlanSelection } from "../../types/plans";
import { config } from "../../config/environment";
import { 
  setLoading, 
  setPlans, 
  setError, 
  selectPlan,
  updateSeatCount as updateSeatCountAction,
  updateBillingCycle as updateBillingCycleAction,
  setCurrency,
} from "./reducer";

// Fetch all active public plans with optional currency filter
export const fetchPlans = (currency?: 'USD' | 'INR', billingCycle?: 'MONTHLY' | 'YEARLY') => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));
    
    // Build URL with query parameters
    const queryParams = new URLSearchParams();
    if (currency) {
      queryParams.append('currency', currency);
    }
    if (billingCycle) {
      queryParams.append('billingCycle', billingCycle);
    }
    
    const url = `${config.api.plans}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    console.log('Fetching plans from:', url);
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch plans: ${response.status}`);
    }
    const plans = await response.json();
    
    // Plans already have calculated pricing from the API
    dispatch(setPlans(plans));
    
    // Auto-select popular plan if available
    const popularPlan = plans.find((plan: SubscriptionPlan) => 
      plan.metadata?.isPopular
    );
    
    if (popularPlan) {
      dispatch(selectPlanAction(popularPlan.slug, popularPlan.limits.minSeats, 'MONTHLY'));
    }
    
  } catch (error: any) {
    dispatch(setError(error.message || 'Failed to fetch plans'));
    toast.error('Failed to load subscription plans');
  } finally {
    dispatch(setLoading(false));
  }
};

// Select a plan with initial configuration
export const selectPlanAction = (
  planSlug: string, 
  seatCount: number, 
  billingCycle: 'MONTHLY' | 'YEARLY'
) => (dispatch: any, getState: any) => {
  try {
    const { plans } = getState().plans;
    const plan = plans.find((p: SubscriptionPlan) => p.slug === planSlug);
    
    if (!plan) {
      dispatch(setError('Selected plan not found'));
      return;
    }
    
    // Validate seat count
    const validSeatCount = Math.max(seatCount, plan.limits.minSeats);
    if (plan.limits.maxSeats && validSeatCount > plan.limits.maxSeats) {
      toast.error(`Maximum ${plan.limits.maxSeats} seats allowed for this plan`);
      return;
    }
    
    // Calculate price
    const pricePerSeat = billingCycle === 'YEARLY' 
      ? plan.pricing.yearlyPricePerSeat
      : plan.pricing.basePricePerSeat;
    
    const selection: PlanSelection = {
      planSlug,
      seatCount: validSeatCount,
      billingCycle,
      totalPrice: pricePerSeat * validSeatCount,
      selectedPlan: plan,
    };
    
    dispatch(selectPlan(selection));
    
  } catch (error: any) {
    dispatch(setError(error.message || 'Failed to select plan'));
    toast.error('Failed to select plan');
  }
};

// Update seat count with validation
export const updateSeatCount = (newSeatCount: number) => (dispatch: any, getState: any) => {
  try {
    const { selectedPlan, plans } = getState().plans;
    
    if (!selectedPlan) {
      dispatch(setError('No plan selected'));
      return;
    }
    
    const plan = plans.find((p: SubscriptionPlan) => p.slug === selectedPlan.planSlug);
    if (!plan) {
      dispatch(setError('Selected plan not found'));
      return;
    }
    
    // Validate seat count
    if (newSeatCount < plan.limits.minSeats) {
      toast.error(`Minimum ${plan.limits.minSeats} seats required for this plan`);
      return;
    }
    
    if (plan.limits.maxSeats && newSeatCount > plan.limits.maxSeats) {
      toast.error(`Maximum ${plan.limits.maxSeats} seats allowed for this plan`);
      return;
    }
    
    dispatch(updateSeatCountAction(newSeatCount));
    
  } catch (error: any) {
    dispatch(setError(error.message || 'Failed to update seat count'));
    toast.error('Failed to update seat count');
  }
};

// Update billing cycle
export const updateBillingCycle = (billingCycle: 'MONTHLY' | 'YEARLY') => (dispatch: any) => {
  try {
    dispatch(updateBillingCycleAction(billingCycle));
  } catch (error: any) {
    dispatch(setError(error.message || 'Failed to update billing cycle'));
    toast.error('Failed to update billing cycle');
  }
};

// Update selected currency and refetch plans
export const updateCurrency = (currency: 'USD' | 'INR') => async (dispatch: any, getState: any) => {
  try {
    console.log('Updating currency to:', currency);
    
    // Update currency in state (this will clear selected plan)
    dispatch(setCurrency(currency));
    
    // Get current billing cycle to maintain selection
    const { selectedPlan } = getState().plans;
    const currentBillingCycle = selectedPlan?.billingCycle || 'MONTHLY';
    
    // Refetch plans with new currency
    await dispatch(fetchPlans(currency, currentBillingCycle));
  } catch (error: any) {
    dispatch(setError(error.message || 'Failed to update currency'));
    toast.error('Failed to update currency');
  }
};

// Create subscription (for later steps)
export const createSubscription = (subscriptionData: any) => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));
    
    const response = await fetch('/api/subscriptions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscriptionData),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create subscription: ${response.status}`);
    }
    
    const result = await response.json();
    
    toast.success('Subscription created successfully!');
    return result;
    
  } catch (error: any) {
    dispatch(setError(error.message || 'Failed to create subscription'));
    toast.error('Failed to create subscription');
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};